import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader2 } from 'lucide-react';
import { useApi, API_ENDPOINTS } from '../lib/api';
import { setLanding } from '../lib/landing';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0();
  const isImpersonating =
    typeof window !== 'undefined' &&
    !!sessionStorage.getItem('impersonateUserId');
  const [profileStatus, setProfileStatus] = useState<{
    profileCompleted: boolean;
    requiresSignup: boolean;
    loading: boolean;
  }>({
    profileCompleted: false,
    requiresSignup: false,
    loading: true
  });
  const api = useApi();
  const location = useLocation();
  
  // Check profile completion status when user is authenticated
  useEffect(() => {
    const checkProfileStatus = async () => {
      if (!isAuthenticated || isLoading) {
        // Auth0 has settled but there is no session (e.g. an admin browsing via
        // impersonation): there is nothing to fetch, so release anyone waiting
        // on the landing answer rather than making them sit out the timeout.
        if (!isLoading) setLanding(null);
        setProfileStatus(prev => ({ ...prev, loading: false }));
        return;
      }
      
      try {
        console.log('🔍 [PROTECTED_ROUTE] Checking profile completion status...');
        const response = await api.get(API_ENDPOINTS.AUTH_PROFILE_STATUS);
        console.log('📊 [PROTECTED_ROUTE] Profile status:', response.data);

        // Publish the server's landing decision for the page context/selector.
        // No redirect here on purpose: this wraps the entire /* subtree, so
        // enforcing a landing route would hijack every deep link and every
        // non-page screen (/settings, "Manage Pages", ...). The login entry
        // points own the redirect; PageRouteGuard owns bad page URLs.
        setLanding(response.data.landing ?? null);

        setProfileStatus({
          profileCompleted: response.data.profileCompleted,
          requiresSignup: response.data.requiresSignup,
          loading: false
        });
      } catch (error: any) {
        console.error('❌ [PROTECTED_ROUTE] Error checking profile status:', error);

        // Unblock anything awaiting the landing answer rather than let it hang.
        setLanding(null);
        
        // For network errors or temporary failures, don't assume signup is required
        // Only redirect to signup for specific 401/404 errors that indicate missing user
        const shouldRequireSignup = error?.response?.status === 401 || 
                                   error?.response?.status === 404 ||
                                   (error?.response?.data?.message && 
                                    error?.response?.data?.message?.includes('User not found'));
        
        if (shouldRequireSignup) {
          console.log('🔄 [PROTECTED_ROUTE] User not found, requiring signup');
          setProfileStatus({
            profileCompleted: false,
            requiresSignup: true,
            loading: false
          });
        } else {
          console.log('⚠️ [PROTECTED_ROUTE] Temporary error, allowing access');
          // For network errors or server issues, allow access and try again later
          setProfileStatus({
            profileCompleted: true, // Assume completed to avoid blocking user
            requiresSignup: false,
            loading: false
          });
        }
      }
    };

    checkProfileStatus();
  }, [isAuthenticated, isLoading]); // Removed 'api' from dependencies to prevent infinite loop
  
  // Admin impersonation bypass: render children directly
  if (isImpersonating) {
    return <>{children}</>;
  }

  // Show loading state while Auth0 is checking authentication
  if (isLoading || (isAuthenticated && profileStatus.loading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check Auth0 authentication
  if (!isAuthenticated) {
    console.log('🔐 [PROTECTED_ROUTE] User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // If authenticated but profile not completed, redirect to signup
  if (isAuthenticated && profileStatus.requiresSignup) {
    const currentPath = location.pathname + location.search;
    console.log('🔄 [PROTECTED_ROUTE] Profile incomplete, redirecting to signup from:', currentPath);
    return <Navigate to={`/signup?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  // Render protected content
  console.log('✅ [PROTECTED_ROUTE] Profile completed, allowing access');
  return <>{children}</>;
};

export default ProtectedRoute;